import { JwtVerifiedCredential } from '@dynamic-labs/sdk-api';
import {
  useDynamicContext,
  useUserWallets,
} from '@dynamic-labs/sdk-react-core';
import { useMemo } from 'react';

import { AvatarEvmNft, Metadata } from '@repo/types-client';

import { useEvmNfts } from '../evm/hooks';
import { useAvatarWallets } from './use-avatar-wallets';
import { useBasicUser } from './use-basic-user';
import { useHasDefaultAddress } from './use-has-default-address';
import { useHasMultipleAvatarWallets } from './use-has-multiple-avatar-wallets';
import { useHasNonZeroDevAndDynamicWallet } from './use-has-non-zero-dev-and-dynamic-wallet';
import { useHasSingleAvatarWallet } from './use-has-single-avatar-wallet';
import { useMostRecentlySelectedWalletWithAvatars } from './use-most-recently-selected-wallet-with-avatars';

/**
 * Custom hook to determine the receiver address based on various conditions.
 * @function useReceiverAddress
 * @returns {object} An object containing the receiver address.
 */
export const useReceiverAddress = () => {
  const { user } = useDynamicContext();
  const userWallets = useUserWallets();
  const basicUser = useBasicUser(user);

  const { data: avatars } = useEvmNfts<AvatarEvmNft>({
    addresses: userWallets.map((wallet) => wallet.address) || [],
    collections: ['Avatar'],
    chain: 'base',
    dataSource: 'indexer',
  });

  const avatarWallets = useAvatarWallets(avatars);
  const mostRecentlySelectedWalletWithAvatars =
    useMostRecentlySelectedWalletWithAvatars(avatarWallets, user);
  const hasDefaultAddress = useHasDefaultAddress(userWallets, user);
  const hasSingleAvatarWallet = useHasSingleAvatarWallet(avatarWallets);
  const hasMultipleAvatarWallets = useHasMultipleAvatarWallets(avatarWallets);
  const hasNonZeroDevAndDynamicWallet = useHasNonZeroDevAndDynamicWallet(user);

  const receiverAddress = useMemo(() => {
    if (hasDefaultAddress) {
      return (user?.metadata as Metadata)?.defaultAddress;
    }

    if (hasSingleAvatarWallet) {
      return avatarWallets[0];
    }

    if (hasMultipleAvatarWallets) {
      return mostRecentlySelectedWalletWithAvatars;
    }

    if (basicUser) {
      return (user?.verifiedCredentials as JwtVerifiedCredential[])?.find(
        (cred: JwtVerifiedCredential) =>
          cred.walletName === 'turnkeyhd' || cred.walletName === 'dynamicwaas',
      )?.address;
    }

    if (hasNonZeroDevAndDynamicWallet) {
      return hasNonZeroDevAndDynamicWallet;
    }

    const firstVerifiedCredential = user?.verifiedCredentials.find(
      (cred) => cred.address !== undefined,
    );
    return firstVerifiedCredential?.address;
  }, [
    hasDefaultAddress,
    hasSingleAvatarWallet,
    hasMultipleAvatarWallets,
    basicUser,
    hasNonZeroDevAndDynamicWallet,
    user?.metadata,
    user?.verifiedCredentials,
    avatarWallets,
    mostRecentlySelectedWalletWithAvatars,
  ]);

  return {
    receiverAddress: receiverAddress?.toLowerCase(),
  };
};
